import { UserEnteredLogicInputs, getWebPath, isLookupCellData } from '@doss/common-all'
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size as sizeMiddleware,
  useFloating,
  useMergeRefs,
} from '@floating-ui/react'
import { compact, get, isEmpty, isNil, orderBy } from 'lodash'
import { CornerDownLeft } from 'lucide-react'
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { RxMagnifyingGlass } from 'react-icons/rx'
import { Else, If, Then } from 'react-if'
import { cn } from '~/@/lib/utils'
import {
  BreadcrumbObjectType,
  Table,
  TableColumn,
  useGetDetailedTableQuery,
} from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { controlKey } from '~/common/utils/keymap'
import { Button } from '~/components/ui/atoms/button/Button'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { LookupCellChipV2 } from '../../ui/molecules/lookup-cell-chip/LookupCellChipV2'
import { DraggableResizablePopover } from './DraggableResizablePopover'
import { useLookupContext } from './hooks/LookupProvider'
import { LookupState, LookupValue } from './LookupCell'
import { LookupDialogHeader } from './LookupDialogHeader'
import { LookupFilesInline } from './LookupFilesInline'
import {
  type LookupFilterFieldMaps,
  getLookupFilterFieldNameMapTablePart,
  mergeLookupFilterFieldMaps,
} from './lookupFilterFieldMaps'
import { LookupRefinementPanel } from './LookupRefinementPanel'
import { LookupRowOptionsList } from './LookupRowOptionsList'
import { useLookupRefinement } from './useLookupRefinement'

type LookupRowSearchDetails = {
  onChange: (newOption: LookupState) => void
  className?: string
  column: Pick<TableColumn, 'id' | 'name' | 'type'>
  isArray: boolean
  lookupTable?: Pick<Table, 'id' | 'name'> & {
    parentTable?: { id: string } | null
  }
  values?: LookupValue | null
  parentRowId?: string
  getUserEnteredValues: () => UserEnteredLogicInputs
  lookupFilterExpressionId?: string
  lookupFilterExpressionString?: string | null
  disabled?: boolean
  autoFocus?: boolean
  autoSave?: boolean
  isBorderless?: boolean
  isUnlinkedLookup: boolean
  lookupState: LookupState
  setLookupState?: (newState: LookupState) => void
  lookupColumnGroupLookupRowIds?: string[]
  categoryHasHiddenFieldsWithLookupColumn?: boolean
  loadingLookupGroup?: boolean
  error?: string
  hideInnerError?: boolean
  hideSearchIcon?: boolean
  placeholder?: string
  onClose?: () => void
  hideUnlinkLookupButton?: boolean
  /** Optional form filter maps to merge with table maps when lookupTable is set (e.g. FormV2). Filter maps are derived from detailed table fetch when lookup is opened. */
  formLookupFilterMaps?: LookupFilterFieldMaps
  /**
   * When true, show the collapsible "Filter & sort" panel so users can layer ad-hoc filters/sorts
   * on the picker (matching the bulk "Insert Multiple Rows" dialog). Off by default so existing
   * lookup-cell popups are unchanged.
   */
  enableFilterSortRefinement?: boolean
}

/** Trigger + popover (mobile: modal) wrapper for the lookup picker */
export function LookupRowSearch({
  onChange,
  className,
  column,
  isArray,
  values, // Selected cells or overwrite value
  lookupTable,
  lookupState, // Selected rows or overwrite value
  setLookupState,
  parentRowId,
  getUserEnteredValues,
  lookupFilterExpressionId,
  lookupFilterExpressionString,
  disabled,
  autoFocus,
  autoSave,
  isBorderless,
  isUnlinkedLookup,
  lookupColumnGroupLookupRowIds,
  categoryHasHiddenFieldsWithLookupColumn = false,
  loadingLookupGroup,
  error,
  hideInnerError = false,
  hideSearchIcon = false,
  placeholder,
  onClose,
  hideUnlinkLookupButton = false,
  formLookupFilterMaps,
  enableFilterSortRefinement = false,
}: LookupRowSearchDetails) {
  const lookupTableId = lookupTable?.id
  const { t } = useTypedTranslation()
  const { isUnauth, getCellDisplay, getEnumSource, displayImagesInline } = useLookupContext()
  const { isMobile } = useDeviceType()
  const [isExpanded, setIsExpanded] = useState(autoFocus)
  const anchorEl = useRef<HTMLDivElement>(null)

  // Fetch detailed table only when this lookup is opened (avoids N queries for N cells/inputs)
  const { data: detailedTableData } = useGetDetailedTableQuery({
    variables: { tableId: lookupTableId ?? '' },
    skip: !lookupTableId || !isExpanded,
  })
  const table = detailedTableData?.table

  const effectiveFilterMaps = useMemo(() => {
    if (!lookupTableId || !table) {
      return null
    }
    const tableMaps = getLookupFilterFieldNameMapTablePart(table, t)
    if (formLookupFilterMaps) {
      return mergeLookupFilterFieldMaps(formLookupFilterMaps, tableMaps)
    }
    return tableMaps
  }, [lookupTableId, table, formLookupFilterMaps, t])

  const effectiveFieldNameMap = effectiveFilterMaps?.fieldNameMap
  const effectiveFieldContextNameMap = effectiveFilterMaps?.fieldContextNameMap
  const effectiveFieldContextTypeMap = effectiveFilterMaps?.fieldContextTypeMap

  // Ad-hoc filter/sort refinement layered on top of the configured lookup filter expression.
  // Fetches its supporting data only when enabled and the picker is open.
  const refinement = useLookupRefinement({
    lookupTableId,
    enabled: isExpanded === true && enableFilterSortRefinement,
  })

  // Get fresh userEnteredValues when dropdown opens
  // This ensures lookup filters use the latest values, preventing stale closures
  const userEnteredValues = useMemo(
    () => (isExpanded ? getUserEnteredValues() : {}),
    [isExpanded, getUserEnteredValues],
  )

  const selectedCells = useMemo(() => {
    if (isNil(lookupState) || !Array.isArray(lookupState)) {
      return null
    }
    const currentSelectedCells = lookupState.map((row) => {
      return row.cells.find((rowCell) => {
        return rowCell.column.id === column.id
      })
    })

    return compact(currentSelectedCells)
  }, [lookupState, column.id])

  const cellDisplays = useMemo<{ [key: string]: string } | null>(() => {
    if (!values || !Array.isArray(values)) {
      return null
    }

    return values.reduce((acc, cell) => {
      const display = getCellDisplay(cell, column.id)
      return { ...acc, [cell.id]: display }
    }, {})
  }, [getCellDisplay, values, column.id])

  const isUnchanged = (): boolean => {
    // 1. Handle cases with no selected cells and empty overwritten value
    if (isNil(values)) {
      // Return false if value was previously null but now either contains lookup cells or
      // a user entered value to overwrite the lookup
      return isNil(lookupState)
    }

    // 2. Handle cases where the cell previously had selected lookup cells
    if (Array.isArray(values)) {
      // If the user is now overwriting the lookup, return false
      if (!Array.isArray(lookupState)) {
        return false
      }

      // If the user is not overwriting the lookup, check if the previously selected cell ids match
      // the current selected cell ids

      const previouslySelectedCellIds = values.map(
        (previouslySelectedCell) => previouslySelectedCell.id,
      )
      if (!selectedCells) {
        // If there were previously selected lookup cells but now the value is null, return false
        return false
      }
      // Check if different number of selected cells
      if (previouslySelectedCellIds.length !== selectedCells.length) {
        return false
      }

      const selectedCellIds = selectedCells.map((cell) => cell.id)

      // Check if both arrays contain exactly the same elements (order doesn't matter)
      return (
        new Set([...previouslySelectedCellIds, ...selectedCellIds]).size ===
        previouslySelectedCellIds.length
      )
    }

    // 3. Handle cases where the cell previously was overwriting the lookup
    if (selectedCells) {
      // If the user was previously overwriting the lookup and now the cell has lookup cells,
      // return false
      return false
    }
    if (!Array.isArray(lookupState)) {
      // If the user is still overwriting the value, check if the previously entered value
      // matches the current value
      return values.data === lookupState?.data
    }

    // 4. Default case: values don't match any expected format
    return false
  }

  const {
    refs: floatingRefs,
    floatingStyles,
    isPositioned,
    context,
  } = useFloating({
    open: isExpanded,
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      sizeMiddleware({
        padding: 8,
        apply({ availableWidth, availableHeight, elements }) {
          elements.floating.style.maxWidth = `${String(availableWidth)}px`
          elements.floating.style.maxHeight = `${String(availableHeight)}px`
        },
      }),
    ],
  })

  const triggerRef = useMergeRefs([anchorEl, floatingRefs.setReference])

  const handleClose = () => {
    setIsExpanded(false)
    refinement.reset()
    onClose?.()
    // Return focus to the originating cell on close. The non-refinement popover gets this from
    // FloatingFocusManager's returnFocus; the draggable refinement frame can't be wrapped in one (its
    // floating element would be hijacked by the shared useFloating size middleware), so restore focus
    // manually for that path. handleClose is the only close path, so this covers every case.
    if (enableFilterSortRefinement) {
      anchorEl.current?.focus()
    }
  }

  const handleSave = () => {
    if (!lookupState) {
      onChange(null)
      handleClose()
      return
    }

    if (Array.isArray(lookupState)) {
      // Set the new options sorted by name
      onChange(orderBy(lookupState, (option) => option.name))
      handleClose()
      return
    }

    onChange(lookupState)
    handleClose()

    return
  }

  const isOverwritingLookup = useMemo(() => {
    return lookupState && typeof lookupState === 'object' && 'data' in lookupState
  }, [lookupState])

  useEffect(() => {
    if (autoSave && lookupState && !isUnchanged() && !isOverwritingLookup) {
      handleSave()
    }
    // When selected option changes, these other dependencies will have the value we want to save with
    // Do not change the dependency array as it will cause unnecessary re-renders
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupState, isOverwritingLookup])

  const renderValue = () => {
    if (isEmpty(values) || isNil(values)) {
      return <div className="text-sm text-gray-8">{placeholder}</div>
    }

    if (Array.isArray(values)) {
      if (displayImagesInline) {
        return values.map((value) => (
          <LookupFilesInline
            key={value.id}
            documentIds={value.documents.map((document) => document.id)}
          />
        ))
      }

      // Delimits the linked records of an array lookup. Enum values ignore this and render as enum chips
      const lookupCellChipType = isArray ? 'chip' : 'text'

      return values.map((value) => {
        const label = get(cellDisplays, value.id, null) ?? ''
        return (
          <LookupCellChipV2
            key={value.id}
            allowNavigate={!isUnauth}
            disabled={disabled}
            enumSource={{
              cell: value,
              column: getEnumSource(value, column.id),
            }}
            lookup={{
              id: value.id,
              value: label,
              rowId: value.row.id,
              column,
              documents: value.documents,
            }}
            textSize="sm"
            type={lookupCellChipType}
          />
        )
      })
    }

    // Handle when lookup is overwritten by displaying just the text
    if (isLookupCellData(values) && values.isOverwritten) {
      return <div className="text-sm">{values.data ?? ''}</div>
    }

    // Handle when the user does not have access to the lookup cell
    if (isLookupCellData(values) && !values.isOverwritten && values.data) {
      return (
        <div className="flex flex-row items-center gap-2">
          <LookupCellChipV2
            allowNavigate={false}
            disabled={disabled}
            lookup={{
              value: values.data.toString(),
            }}
            textSize="sm"
          />
        </div>
      )
    }

    return null
  }

  const handleNavigate = (event: MouseEvent<HTMLElement>) => {
    if (!lookupTable || isUnauth) {
      return
    }
    const isLookupOnChildTable = !isNil(lookupTable.parentTable)

    // If creating a child lookup on parent, route the user to the record
    if (parentRowId && isLookupOnChildTable) {
      event.stopPropagation()
      const path = getWebPath({
        objectType: BreadcrumbObjectType.TABLE_ROW,
        objectId: parentRowId,
      })
      window.open(path, '_blank')
    }

    event.stopPropagation()
    const path = getWebPath({
      objectType: BreadcrumbObjectType.TABLE,
      objectId: lookupTable.id,
    })
    window.open(path, '_blank')
  }

  // Shared between the mobile modal and desktop popover. When refinement is enabled it flexes to
  // fill the space left by the Filter & sort panel; otherwise it renders exactly as before.
  const lookupOptionsList = (
    <LookupRowOptionsList
      autoFocus={autoFocus}
      autoSave={autoSave}
      categoryHasHiddenFieldsWithLookupColumn={categoryHasHiddenFieldsWithLookupColumn}
      className={enableFilterSortRefinement ? 'min-h-0 flex-1' : undefined}
      column={column}
      error={error}
      fillHeight={enableFilterSortRefinement}
      hideInnerError={hideInnerError}
      hideLookupDialogHeader={enableFilterSortRefinement}
      hideUnlinkLookupButton={hideUnlinkLookupButton}
      isArray={isArray}
      isUnlinkedLookup={isUnlinkedLookup}
      loadingLookupGroup={loadingLookupGroup}
      lookupColumnGroupLookupRowIds={lookupColumnGroupLookupRowIds}
      lookupFilterBooleanOperator={refinement.lookupFilterBooleanOperator}
      lookupFilterExpressionId={lookupFilterExpressionId}
      lookupFilterExpressionString={lookupFilterExpressionString}
      lookupFilterFieldContextNameMap={effectiveFieldContextNameMap}
      lookupFilterFieldContextTypeMap={effectiveFieldContextTypeMap}
      lookupFilterFieldNameMap={effectiveFieldNameMap}
      lookupFilters={refinement.lookupFilters}
      lookupSorts={refinement.lookupSorts}
      lookupState={lookupState}
      lookupTable={lookupTable}
      onClose={handleClose}
      onSave={handleSave}
      parentRowId={parentRowId}
      setLookupState={setLookupState ?? onChange}
      userEnteredValues={userEnteredValues}
      values={values}
    />
  )

  const desktopFooter = (
    <div
      className={cn(
        'flex justify-end py-2 px-4 gap-2 sticky bottom-0 bg-white',
        autoSave && !isOverwritingLookup && '!hidden',
      )}
    >
      <Button onClick={handleClose} variant="ghost">
        {t('common.actions.cancel')}
      </Button>
      <Button
        disabled={isUnchanged()}
        endIcon={controlKey(<CornerDownLeft size={14} />)}
        onClick={handleSave}
        variant="primary"
      >
        {t('common.actions.save')}
      </Button>
    </div>
  )

  // Once clicked, create an anchor on the element and then pop up the menu with the lookup options
  return (
    <>
      <div
        ref={triggerRef}
        className={cn(
          'relative group w-full cursor-pointer rounded border min-h-9',
          'flex items-center flex-wrap gap-2 px-2.5 py-2 text-sm',
          disabled
            ? 'bg-gray-1 border-gray-5 text-gray-9 cursor-default'
            : 'bg-white border-gray-4 text-gray-10 hover:border-gray-6',
          error && 'border-red-9 hover:border-red-9',
          isBorderless && 'border-none',
          className,
        )}
        onClick={disabled ? undefined : () => setIsExpanded(true)}
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsExpanded(true)
                }
              }
        }
        tabIndex={disabled ? -1 : 0}
      >
        {renderValue()}
        {!disabled && !hideSearchIcon && (
          <RxMagnifyingGlass className="absolute end-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-10" />
        )}
      </div>
      <If condition={isExpanded}>
        <Then>
          {/* Show full-screen Dialog on mobile */}
          <If condition={isMobile}>
            <Then>
              <Modal
                actions={
                  <Button disabled={isUnchanged()} onClick={handleSave} variant="primary">
                    {t('common.actions.save')}
                  </Button>
                }
                className="[&_.MuiDialog-paper]:m-0"
                fullScreen
                onClose={handleClose}
                open
                padContent={false}
                showCloseButton={false}
                title={
                  <LookupDialogHeader
                    handleClose={handleClose}
                    handleNavigate={handleNavigate}
                    loading={loadingLookupGroup}
                    lookupTable={lookupTable}
                  />
                }
              >
                {enableFilterSortRefinement ? (
                  <div className="flex h-full min-h-0 flex-col">
                    <LookupRefinementPanel className="m-2 mb-0" refinement={refinement} />
                    {lookupOptionsList}
                  </div>
                ) : (
                  lookupOptionsList
                )}
              </Modal>
            </Then>
            <Else>
              <FloatingPortal>
                {isExpanded &&
                  (enableFilterSortRefinement ? (
                    // Draggable + resizable frame so the picker stays usable with Filter & sort open.
                    <DraggableResizablePopover anchorRef={anchorEl} open={isExpanded}>
                      {/* Title + close above Filter & sort, matching the bulk "Select rows" dialog. */}
                      <div className="shrink-0 px-4 pt-3">
                        <LookupDialogHeader
                          handleClose={handleClose}
                          handleNavigate={handleNavigate}
                          loading={loadingLookupGroup}
                          lookupTable={lookupTable}
                        />
                      </div>
                      <LookupRefinementPanel className="mx-2 mt-2" refinement={refinement} />
                      {lookupOptionsList}
                      {desktopFooter}
                    </DraggableResizablePopover>
                  ) : (
                    <FloatingFocusManager context={context}>
                      <div
                        ref={floatingRefs.setFloating}
                        style={{
                          ...floatingStyles,
                          zIndex: 1300,
                          opacity: isPositioned ? 1 : 0,
                          pointerEvents: isPositioned ? undefined : 'none',
                          minWidth: 'min(500px, 100vw - 16px)',
                          width: `${anchorEl.current?.clientWidth ?? 0}px`,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          overflow: 'hidden',
                          borderRadius: '4px',
                          boxShadow:
                            '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                          backgroundColor: '#fff',
                        }}
                      >
                        {lookupOptionsList}
                        {/* Show the footer actions */}
                        {desktopFooter}
                      </div>
                    </FloatingFocusManager>
                  ))}
              </FloatingPortal>
            </Else>
          </If>
        </Then>
        <Else></Else>
      </If>
    </>
  )
}
